import { IAssignmentDTO, ICreateOrUpdateSupplierDTO, IMapData } from '../../../models';
import Utilities from '../../../helpers/Utilities';

const GERMAN_REGION_KEYWORDS = ['germany', 'deutschland', 'tysk', 'german'];

export interface IAssignmentCrudHandler {
	isGermanAssignment(assignment?: IAssignmentDTO, supplier?: ICreateOrUpdateSupplierDTO, fallbackIsGermanUser?: boolean): boolean;
	shouldLoadLevCertData(isGermanAssignment: boolean): boolean;
	shouldShowCfHarvestFields(isGermanAssignment: boolean): boolean;
	prepareAssignmentForCrud(assignment: IAssignmentDTO, isGermanAssignment: boolean): IAssignmentDTO;
	validateGermanAssignment(assignment: IAssignmentDTO): { isValid: boolean; missingFields: string[] };
	isMapValidForGermanApproval(assignment: IAssignmentDTO): boolean;
}

export class GermanAssignmentHandler implements IAssignmentCrudHandler {
	public isGermanAssignment(assignment?: IAssignmentDTO, supplier?: ICreateOrUpdateSupplierDTO, fallbackIsGermanUser: boolean = false): boolean {
		const assignmentRegion = this.normalizeRegionValue(assignment?.regionName);
		const supplierRegion = this.normalizeRegionValue(supplier?.region?.name || supplier?.regionName);

		if (this.matchesGermanRegion(assignmentRegion) || this.matchesGermanRegion(supplierRegion)) {
			return true;
		}

		return fallbackIsGermanUser;
	}

	public shouldLoadLevCertData(isGermanAssignment: boolean): boolean {
		return !isGermanAssignment;
	}

	public shouldShowCfHarvestFields(isGermanAssignment: boolean): boolean {
		return !isGermanAssignment;
	}

	public prepareAssignmentForCrud(assignment: IAssignmentDTO, isGermanAssignment: boolean): IAssignmentDTO {
		if (!isGermanAssignment) {
			return assignment;
		}

		return {
			...assignment,
			// German assignments are handled without LevCert/CF Harvest integrations.
			certification: undefined,
			responsible: undefined,
			trappingWorkPerformedByEntrepreneur: undefined,
			extractionPerformedByEntrepreneur: undefined,
			woodchippingPerformedByEntrepreneur: undefined,
			hasNatureOrCultureBeenDamage: undefined,
			hasTheDamageSubsequentlyBeenRepaired: undefined,
			continuedTreeGrowthOnTheAreaOrReplanting: undefined,
		};
	}

	public validateGermanAssignment(assignment: IAssignmentDTO): { isValid: boolean; missingFields: string[] } {
		const missingFields: string[] = [];

		// Required fields for German assignments
		if (!Utilities.CheckNotNullOrEmpty(assignment.orderName)) {
			missingFields.push("<li>{{validationTaskName}}</li>");
		}

		if (!Utilities.CheckNotNullOrEmpty(assignment.biomassCategory)) {
			missingFields.push("<li>{{validationChipType}}</li>");
		}

		if (assignment.estimatedAmount == null || !Utilities.CheckNotNullOrEmpty(isNaN(assignment.estimatedAmount as number) ? "" : assignment.estimatedAmount + "")) {
			missingFields.push("<li>{{validationEstimatedAmount}}</li>");
		}

		// regionName isn't reliably persisted/returned by the backend on reload, so validate against regionID only
		if (!assignment.regionID) {
			missingFields.push("<li>{{regionsNummerLabel}}</li>");
		}

		if (!assignment.trappingDate) {
			missingFields.push("<li>{{validationTrappingDate}}</li>");
		}

		if (!Utilities.CheckNotNullOrEmpty(assignment.hdaoNo?.toLocaleString())) {
			missingFields.push("<li>{{validationAONumber}}</li>");
		}

		// Map validation
		if (!this.isMapValidForGermanApproval(assignment)) {
			if (!assignment.mapData) {
				missingFields.push("<li>{{validationDrawMap}}</li>");
			} else {
				const mapData = JSON.parse(assignment.mapData) as IMapData;
				if (!mapData.fixedTaskPosition) {
					missingFields.push("<li>{{validationTaskGPS}}</li>");
				}
				if (mapData.workingAreas.length === 0) {
					missingFields.push("<li>{{validationMinWorkingArea}}</li>");
				}
			}
		}

		return {
			isValid: missingFields.length === 0,
			missingFields
		};
	}

	public isMapValidForGermanApproval(assignment: IAssignmentDTO): boolean {
		if (!assignment.mapData) {
			return false;
		}

		try {
			const mapData = JSON.parse(assignment.mapData) as IMapData;
			return mapData.fixedTaskPosition != undefined && mapData.workingAreas.length > 0;
		} catch {
			return false;
		}
	}

	private matchesGermanRegion(regionName?: string): boolean {
		if (!regionName) {
			return false;
		}

		return GERMAN_REGION_KEYWORDS.some(keyword => regionName.indexOf(keyword) > -1);
	}

	private normalizeRegionValue(regionName?: string): string {
		return (regionName || '').trim().toLowerCase();
	}
}
